import React, { useState, useEffect } from 'react';
import { 
  Users, UserCheck, UserMinus, ShieldCheck, ShieldAlert, 
  FileSpreadsheet, Plus, Search, Filter, Clock, X, Info
} from 'lucide-react';
import axios from 'axios';

const AttendanceDashboard = () => {
  const userRole = localStorage.getItem('userRole') || 'VIEWER';
  const isDemo = userRole === 'DEMO_USER';
  const canWrite = userRole === 'COMPANY_ADMIN' || userRole === 'SUPER_ADMIN' || userRole === 'PROJECT_MANAGER';

  const [summary, setSummary] = useState({
    activeOnSite: 0,
    checkedOut: 0,
    subcontractorsOnSite: 0,
    ehsComplianceRate: 100
  });
  const [attendance, setAttendance] = useState([]);
  const [subcontractors, setSubcontractors] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [filteredAttendance, setFilteredAttendance] = useState([]);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [subFilter, setSubFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [ehsFilter, setEhsFilter] = useState('');
  
  // Check-in modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedSubcontractor, setSelectedSubcontractor] = useState('');
  const [selectedWorker, setSelectedWorker] = useState('');
  const [ehsPassed, setEhsPassed] = useState(true);
  const [ehsNotes, setEhsNotes] = useState('');
  const [status, setStatus] = useState('present');
  const [formLoading, setFormLoading] = useState(false);

  // New Subcontractor Modal State
  const [subModalOpen, setSubModalOpen] = useState(false);
  const [newSubName, setNewSubName] = useState('');
  const [newSubSpecialty, setNewSubSpecialty] = useState('');
  const [newSubSupervisor, setNewSubSupervisor] = useState('');
  const [newSubPhone, setNewSubPhone] = useState('');

  // New Worker Modal State
  const [workerModalOpen, setWorkerModalOpen] = useState(false);
  const [newWorkerName, setNewWorkerName] = useState('');
  const [newWorkerRole, setNewWorkerRole] = useState('');
  const [newWorkerNss, setNewWorkerNss] = useState('');
  const [newWorkerSubId, setNewWorkerSubId] = useState('');

  const activeProjectName = localStorage.getItem('activeProjectName') || 'Proyecto Sin Asignar';
  const activeProjectId = localStorage.getItem('activeProjectId');

  const headers = {
    Authorization: `Bearer ${localStorage.getItem('token')}`,
    'x-project-id': activeProjectId
  };

  const fetchData = async () => {
    if (!activeProjectId) return;
    try {
      // Get Summary
      const summaryRes = await axios.get('http://localhost:4000/api/attendance/summary', { headers });
      setSummary(summaryRes.data);

      // Get Attendance Logs
      const attendanceRes = await axios.get('http://localhost:4000/api/attendance', { headers });
      setAttendance(attendanceRes.data);
      setFilteredAttendance(attendanceRes.data);

      // Get Subcontractors
      const subRes = await axios.get('http://localhost:4000/api/attendance/subcontractors', { headers });
      setSubcontractors(subRes.data);

      // Get Workers
      const workersRes = await axios.get('http://localhost:4000/api/attendance/workers', { headers });
      setWorkers(workersRes.data);
    } catch (err) {
      console.error("Error loading attendance dashboard data:", err);
    }
  };

  useEffect(() => {
    fetchData();
  }, [activeProjectId]);

  // Apply filters
  useEffect(() => {
    let result = attendance;

    if (searchTerm) {
      result = result.filter(item => 
        item.worker_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.role.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.subcontractor_name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (subFilter) {
      result = result.filter(item => item.subcontractor_id.toString() === subFilter);
    }

    if (statusFilter) {
      result = result.filter(item => item.status === statusFilter);
    }

    if (ehsFilter) {
      const isPassed = ehsFilter === 'passed';
      result = result.filter(item => item.ehs_passed === isPassed);
    }

    setFilteredAttendance(result);
  }, [searchTerm, subFilter, statusFilter, ehsFilter, attendance]);

  const handleCheckIn = async (e) => {
    e.preventDefault();
    if (!selectedWorker) return;
    setFormLoading(true);
    try {
      await axios.post('http://localhost:4000/api/attendance/check-in', {
        worker_id: selectedWorker,
        ehs_passed: ehsPassed,
        ehs_notes: ehsNotes,
        status: status
      }, { headers });
      
      // Reset & close
      setSelectedSubcontractor('');
      setSelectedWorker('');
      setEhsPassed(true);
      setEhsNotes('');
      setStatus('present');
      setModalOpen(false);
      
      // Refresh
      fetchData();
    } catch (err) {
      alert(err.response?.data?.error || "Error al realizar Check-in");
    } finally {
      setFormLoading(false);
    }
  };

  const handleCheckOut = async (attendanceId) => {
    if (!window.confirm("¿Seguro que deseas realizar el check-out de este operario?")) return;
    try {
      await axios.post('http://localhost:4000/api/attendance/check-out', {
        attendance_id: attendanceId
      }, { headers });
      fetchData();
    } catch (err) {
      alert("Error al realizar Check-out");
    }
  };

  const handleCreateSubcontractor = async (e) => {
    e.preventDefault();
    if (!newSubName || !newSubSpecialty) return;
    try {
      await axios.post('http://localhost:4000/api/attendance/subcontractors', {
        name: newSubName,
        specialty: newSubSpecialty,
        supervisor_name: newSubSupervisor,
        contact_phone: newSubPhone
      }, { headers });
      
      setNewSubName('');
      setNewSubSpecialty('');
      setNewSubSupervisor('');
      setNewSubPhone('');
      setSubModalOpen(false);
      fetchData();
    } catch (err) {
      alert("Error al registrar contratista");
    }
  };

  const handleCreateWorker = async (e) => {
    e.preventDefault();
    if (!newWorkerSubId || !newWorkerName || !newWorkerRole) return;
    try {
      await axios.post('http://localhost:4000/api/attendance/workers', {
        subcontractor_id: newWorkerSubId,
        name: newWorkerName,
        role: newWorkerRole,
        nss: newWorkerNss
      }, { headers });
      
      setNewWorkerName('');
      setNewWorkerRole('');
      setNewWorkerNss('');
      setNewWorkerSubId('');
      setWorkerModalOpen(false);
      fetchData();
    } catch (err) {
      alert("Error al registrar trabajador");
    }
  };

  const exportToCSV = () => {
    if (filteredAttendance.length === 0) return;
    
    const csvRows = [];
    // Headers
    const headers_csv = ["ID", "Trabajador", "Puesto", "Contratista", "Especialidad", "Check In", "Check Out", "EHS Estatus", "EHS Notas", "Estatus"];
    csvRows.push(headers_csv.join(','));

    for (const row of filteredAttendance) {
      const values = [
        row.id,
        `"${row.worker_name}"`,
        `"${row.role}"`,
        `"${row.subcontractor_name}"`,
        `"${row.specialty}"`,
        `"${new Date(row.check_in).toLocaleString()}"`,
        row.check_out ? `"${new Date(row.check_out).toLocaleString()}"` : '"ACTIVO"',
        row.ehs_passed ? "APROBADO" : "CON OBSERVACION",
        `"${row.ehs_notes || ''}"`,
        row.status.toUpperCase()
      ];
      csvRows.push(values.join(','));
    }

    const csvContent = "data:text/csv;charset=utf-8,\uFEFF" + csvRows.join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `asistencia_${activeProjectName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Filter workers based on selected subcontractor in check-in form
  const availableWorkers = selectedSubcontractor
    ? workers.filter(w => w.subcontractor_id.toString() === selectedSubcontractor)
    : [];

  return (
    <div className="min-h-screen bg-theme-bg bg-[radial-gradient(ellipse_80%_80%_at_50%_-20%,rgba(16,185,129,0.12),rgba(255,255,255,0))] text-theme-text-primary p-4 md:p-8 font-sans">
      <div className="max-w-7xl mx-auto">
        
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
          <div>
            <h1 className="text-4xl font-extrabold tracking-tight text-theme-text-primary mb-2 flex items-center gap-3">
              <Users className="text-emerald-500" size={36} /> Control de Acceso y Asistencia de Cuadrillas
            </h1>
            <p className="text-theme-text-secondary font-medium">
              Administración de contratistas en obra, control de check-in/out, auditoría rápida de EPP e historial.
            </p>
          </div>
          {canWrite ? (
            <div className="flex gap-2">
              <button
                onClick={() => setSubModalOpen(true)}
                className="bg-theme-panel hover:bg-theme-border text-theme-text-primary text-xs font-bold px-4 py-2.5 rounded-2xl border border-theme-border transition-all flex items-center gap-2"
              >
                + Contratista
              </button>
              <button
                onClick={() => setWorkerModalOpen(true)}
                className="bg-theme-panel hover:bg-theme-border text-theme-text-primary text-xs font-bold px-4 py-2.5 rounded-2xl border border-theme-border transition-all flex items-center gap-2"
              >
                + Trabajador
              </button>
              <button
                onClick={() => setModalOpen(true)}
                className="bg-gradient-to-r from-emerald-600 to-teal-500 hover:scale-[1.02] text-white text-xs font-bold px-5 py-2.5 rounded-2xl shadow-lg transition-all flex items-center gap-2"
              >
                <UserCheck size={16} /> Registro Check-In
              </button>
            </div>
          ) : isDemo ? (
            <div className="bg-yellow-500/10 border border-yellow-500/20 px-4 py-2 rounded-2xl text-[10px] font-bold text-yellow-500 flex items-center gap-1.5 animate-pulse">
              <ShieldAlert size={12} /> Modo Demo Activo (Lectura)
            </div>
          ) : (
            <div className="bg-blue-500/10 border border-blue-500/20 px-4 py-2 rounded-2xl text-[10px] font-bold text-blue-400 flex items-center gap-1.5">
              <Info size={12} /> Modo Lector (Solo Vista)
            </div>
          )}
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          
          <div className="bg-theme-panel p-6 rounded-3xl border border-theme-border flex items-center justify-between">
            <div>
              <span className="text-xs font-bold text-theme-text-secondary uppercase tracking-widest block mb-1">Activos en Obra</span>
              <span className="text-3xl font-black text-emerald-400">{summary.activeOnSite}</span>
            </div>
            <div className="h-12 w-12 bg-emerald-500/10 rounded-2xl flex items-center justify-center text-emerald-400">
              <UserCheck size={24} />
            </div>
          </div>

          <div className="bg-theme-panel p-6 rounded-3xl border border-theme-border flex items-center justify-between">
            <div>
              <span className="text-xs font-bold text-theme-text-secondary uppercase tracking-widest block mb-1">Egresos Hoy</span>
              <span className="text-3xl font-black text-blue-400">{summary.checkedOut}</span>
            </div>
            <div className="h-12 w-12 bg-blue-500/10 rounded-2xl flex items-center justify-center text-blue-400">
              <UserMinus size={24} />
            </div>
          </div>

          <div className="bg-theme-panel p-6 rounded-3xl border border-theme-border flex items-center justify-between">
            <div>
              <span className="text-xs font-bold text-theme-text-secondary uppercase tracking-widest block mb-1">Contratistas Activos</span>
              <span className="text-3xl font-black text-purple-400">{summary.subcontractorsOnSite}</span>
            </div>
            <div className="h-12 w-12 bg-purple-500/10 rounded-2xl flex items-center justify-center text-purple-400">
              <Users size={24} />
            </div>
          </div>

          <div className="bg-theme-panel p-6 rounded-3xl border border-theme-border flex items-center justify-between">
            <div>
              <span className="text-xs font-bold text-theme-text-secondary uppercase tracking-widest block mb-1">Cumplimiento EPP</span>
              <span className="text-3xl font-black text-amber-400">{summary.ehsComplianceRate}%</span>
            </div>
            <div className="h-12 w-12 bg-amber-500/10 rounded-2xl flex items-center justify-center text-amber-400">
              <ShieldCheck size={24} />
            </div>
          </div>

        </div>

        {/* Checked-In Active Workers Section */}
        <div className="mb-8">
          <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
            <Clock size={20} className="text-emerald-400 animate-pulse" /> Personal en Sitio Actualmente
          </h2>
          <div className="bg-theme-panel rounded-3xl border border-theme-border overflow-hidden shadow-xl">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-theme-bg border-b border-theme-border text-xs font-black text-theme-text-secondary uppercase tracking-wider">
                    <th className="p-4 pl-6">Operario</th>
                    <th className="p-4">Contratista</th>
                    <th className="p-4">Hora Ingreso</th>
                    <th className="p-4">Filtro EPP / EHS</th>
                    <th className="p-4">Comentarios</th>
                    <th className="p-4 text-center pr-6">Acción</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-theme-border/50 text-sm">
                  {attendance.filter(a => a.check_out === null && a.status !== 'absent').length === 0 ? (
                    <tr>
                      <td colSpan="6" className="p-8 text-center text-theme-text-secondary">
                        <Info size={32} className="mx-auto text-slate-500 mb-2 opacity-50" />
                        <span className="font-bold text-theme-text-primary block text-sm">No hay personal activo en sitio</span>
                        <span className="text-xs">Registra un Check-In para empezar a monitorear la asistencia.</span>
                      </td>
                    </tr>
                  ) : (
                    attendance.filter(a => a.check_out === null && a.status !== 'absent').map((row) => (
                      <tr key={row.id} className="hover:bg-theme-border/10 transition-colors">
                        <td className="p-4 pl-6">
                          <div className="font-bold text-theme-text-primary">{row.worker_name}</div>
                          <div className="text-xs text-theme-text-secondary">{row.role}</div>
                        </td>
                        <td className="p-4">
                          <div className="font-semibold">{row.subcontractor_name}</div>
                          <div className="text-xs text-theme-text-secondary">{row.specialty}</div>
                        </td>
                        <td className="p-4 font-mono text-xs">
                          {new Date(row.check_in).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          {row.status === 'late' && <span className="ml-2 text-[9px] bg-red-500/10 text-red-400 px-1.5 py-0.5 rounded border border-red-500/20 font-bold uppercase">Tarde</span>}
                        </td>
                        <td className="p-4">
                          {row.ehs_passed ? (
                            <span className="inline-flex items-center gap-1.5 text-xs text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded-full border border-emerald-500/20 font-semibold">
                              <ShieldCheck size={14} /> Pasa Filtro
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 text-xs text-amber-400 bg-amber-500/10 px-2 py-0.5 rounded-full border border-amber-500/20 font-semibold">
                              <ShieldAlert size={14} /> Observación
                            </span>
                          )}
                        </td>
                        <td className="p-4 text-xs max-w-xs truncate text-theme-text-secondary">{row.ehs_notes || '-'}</td>
                        <td className="p-4 text-center pr-6">
                          {canWrite ? (
                            <button
                              onClick={() => handleCheckOut(row.id)}
                              className="bg-red-500/10 hover:bg-red-500/20 text-red-400 text-xs font-bold px-3 py-1.5 rounded-xl border border-red-500/20 transition-all"
                            >
                              Registrar Salida
                            </button>
                          ) : (
                            <span className="text-xs text-slate-500">-</span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Historical Attendance Section */}
        <div>
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 gap-4">
            <h2 className="text-xl font-bold flex items-center gap-2">
              <FileSpreadsheet size={20} className="text-theme-text-secondary" /> Histórico y Registro de Asistencia
            </h2>
            <div className="flex gap-2 w-full md:w-auto">
              <button
                onClick={exportToCSV}
                className="bg-theme-panel hover:bg-theme-border text-theme-text-primary text-xs font-bold px-4 py-2.5 rounded-2xl border border-theme-border transition-all flex items-center gap-2"
              >
                <FileSpreadsheet size={16} /> Exportar CSV
              </button>
            </div>
          </div>

          {/* Filters Bar */}
          <div className="bg-theme-panel p-4 rounded-3xl border border-theme-border mb-4 grid grid-cols-1 md:grid-cols-4 gap-4 shadow-md">
            
            <div className="relative">
              <Search className="absolute left-3 top-2.5 text-slate-500" size={16} />
              <input
                type="text"
                placeholder="Buscar operario, contratista..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full bg-theme-bg border border-theme-border rounded-xl py-1.5 pl-10 pr-4 text-sm text-theme-text-primary placeholder:text-slate-500 focus:outline-none focus:border-emerald-500/50"
              />
            </div>

            <div className="relative">
              <Filter className="absolute left-3 top-2.5 text-slate-500" size={16} />
              <select
                value={subFilter}
                onChange={(e) => setSubFilter(e.target.value)}
                className="w-full bg-theme-bg border border-theme-border rounded-xl py-1.5 pl-10 pr-4 text-sm text-theme-text-primary appearance-none focus:outline-none focus:border-emerald-500/50"
              >
                <option value="">Todos los contratistas</option>
                {subcontractors.map(sub => (
                  <option key={sub.id} value={sub.id}>{sub.name}</option>
                ))}
              </select>
            </div>

            <div className="relative">
              <Filter className="absolute left-3 top-2.5 text-slate-500" size={16} />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full bg-theme-bg border border-theme-border rounded-xl py-1.5 pl-10 pr-4 text-sm text-theme-text-primary appearance-none focus:outline-none focus:border-emerald-500/50"
              >
                <option value="">Todos los estatus</option>
                <option value="present">Presente (A tiempo)</option>
                <option value="late">Tarde</option>
                <option value="absent">Inasistencia (Ausente)</option>
              </select>
            </div>

            <div className="relative">
              <Filter className="absolute left-3 top-2.5 text-slate-500" size={16} />
              <select
                value={ehsFilter}
                onChange={(e) => setEhsFilter(e.target.value)}
                className="w-full bg-theme-bg border border-theme-border rounded-xl py-1.5 pl-10 pr-4 text-sm text-theme-text-primary appearance-none focus:outline-none focus:border-emerald-500/50"
              >
                <option value="">Filtro de Seguridad (Todos)</option>
                <option value="passed">EHS Aprobado</option>
                <option value="warning">EHS Observado</option>
              </select>
            </div>

          </div>

          {/* Historical Table */}
          <div className="bg-theme-panel rounded-3xl border border-theme-border overflow-hidden shadow-lg">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-theme-bg border-b border-theme-border text-xs font-black text-theme-text-secondary uppercase tracking-wider">
                    <th className="p-4 pl-6">Operario</th>
                    <th className="p-4">Contratista</th>
                    <th className="p-4">Fecha</th>
                    <th className="p-4">Ingreso</th>
                    <th className="p-4">Salida</th>
                    <th className="p-4">EHS Seguridad</th>
                    <th className="p-4 pr-6">Estatus</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-theme-border/50 text-sm">
                  {filteredAttendance.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="p-8 text-center text-theme-text-secondary">
                        No se encontraron registros en el historial.
                      </td>
                    </tr>
                  ) : (
                    filteredAttendance.map((row) => (
                      <tr key={row.id} className="hover:bg-theme-border/5 transition-colors">
                        <td className="p-4 pl-6">
                          <div className="font-bold text-theme-text-primary">{row.worker_name}</div>
                          <div className="text-xs text-theme-text-secondary">{row.role}</div>
                        </td>
                        <td className="p-4">
                          <div className="font-semibold">{row.subcontractor_name}</div>
                          <div className="text-xs text-theme-text-secondary">{row.specialty}</div>
                        </td>
                        <td className="p-4 font-mono text-xs">
                          {new Date(row.check_in).toLocaleDateString([], { day: '2-digit', month: '2-digit', year: 'numeric' })}
                        </td>
                        <td className="p-4 font-mono text-xs">
                          {new Date(row.check_in).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </td>
                        <td className="p-4 font-mono text-xs text-theme-text-secondary">
                          {row.check_out 
                            ? new Date(row.check_out).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                            : (row.status === 'absent' ? '-' : <span className="text-emerald-400 font-bold">En Sitio</span>)
                          }
                        </td>
                        <td className="p-4">
                          {row.ehs_passed ? (
                            <span className="text-xs text-emerald-400 font-bold">OK</span>
                          ) : (
                            <span className="text-xs text-amber-400 font-bold flex flex-col">
                              <span>Observado</span>
                              <span className="text-[10px] text-slate-500 font-normal">{row.ehs_notes}</span>
                            </span>
                          )}
                        </td>
                        <td className="p-4 pr-6">
                          <span className={`text-[10px] font-black px-2 py-0.5 rounded border ${
                            row.status === 'present' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' :
                            row.status === 'late' ? 'bg-amber-500/10 text-amber-400 border-amber-500/20' :
                            'bg-red-500/10 text-red-400 border-red-500/20'
                          } uppercase`}>
                            {row.status === 'present' ? 'A Tiempo' : row.status === 'late' ? 'Tarde' : 'Ausente'}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

      </div>

      {/* CHECK-IN MODAL */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center p-4 z-50">
          <div className="bg-theme-panel border border-theme-border w-full max-w-md rounded-3xl p-6 shadow-2xl relative">
            <button 
              onClick={() => setModalOpen(false)}
              className="absolute right-4 top-4 text-slate-500 hover:text-theme-text-primary p-2 hover:bg-theme-border/20 rounded-xl"
            >
              <X size={20} />
            </button>
            <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
              <UserCheck className="text-emerald-400" /> Registro de Ingreso (Check-In)
            </h3>
            
            <form onSubmit={handleCheckIn} className="space-y-4">
              
              <div>
                <label className="block text-xs font-bold uppercase text-theme-text-secondary mb-1">Contratista</label>
                <select
                  required
                  value={selectedSubcontractor}
                  onChange={(e) => {
                    setSelectedSubcontractor(e.target.value);
                    setSelectedWorker('');
                  }}
                  className="w-full bg-theme-bg border border-theme-border rounded-xl p-2.5 text-sm text-theme-text-primary focus:outline-none focus:border-emerald-500/50"
                >
                  <option value="">Selecciona contratista</option>
                  {subcontractors.map(sub => (
                    <option key={sub.id} value={sub.id}>{sub.name} ({sub.specialty})</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold uppercase text-theme-text-secondary mb-1">Operario</label>
                <select
                  required
                  disabled={!selectedSubcontractor}
                  value={selectedWorker}
                  onChange={(e) => setSelectedWorker(e.target.value)}
                  className="w-full bg-theme-bg border border-theme-border rounded-xl p-2.5 text-sm text-theme-text-primary focus:outline-none focus:border-emerald-500/50 disabled:opacity-50"
                >
                  <option value="">Selecciona operario</option>
                  {availableWorkers.map(w => (
                    <option key={w.id} value={w.id}>{w.name} - {w.role}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold uppercase text-theme-text-secondary mb-1">Estatus Ingreso</label>
                  <select
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    className="w-full bg-theme-bg border border-theme-border rounded-xl p-2.5 text-sm text-theme-text-primary focus:outline-none focus:border-emerald-500/50"
                  >
                    <option value="present">A Tiempo</option>
                    <option value="late">Tarde</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold uppercase text-theme-text-secondary mb-1">Filtro EPP / EHS</label>
                  <div className="flex items-center gap-2 h-[42px]">
                    <button
                      type="button"
                      onClick={() => setEhsPassed(!ehsPassed)}
                      className={`flex-1 py-2 px-3 text-xs font-bold rounded-xl border transition-all ${
                        ehsPassed 
                          ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30' 
                          : 'bg-amber-500/10 text-amber-400 border-amber-500/30'
                      }`}
                    >
                      {ehsPassed ? "Aprobado (OK)" : "Con Observaciones"}
                    </button>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold uppercase text-theme-text-secondary mb-1">Comentarios EHS</label>
                <textarea
                  placeholder="Detalles sobre el equipo de protección o condiciones físicas..."
                  value={ehsNotes}
                  onChange={(e) => setEhsNotes(e.target.value)}
                  className="w-full bg-theme-bg border border-theme-border rounded-xl p-2.5 text-sm text-theme-text-primary focus:outline-none focus:border-emerald-500/50 h-20 resize-none"
                />
              </div>

              <button
                type="submit"
                disabled={formLoading || !selectedWorker}
                className="w-full bg-gradient-to-r from-emerald-600 to-teal-500 py-3 text-white rounded-xl font-bold shadow-lg hover:scale-[1.01] transition-all disabled:opacity-50 disabled:hover:scale-100"
              >
                {formLoading ? "Registrando..." : "Confirmar Check-In"}
              </button>

            </form>
          </div>
        </div>
      )}

      {/* NEW SUBCONTRACTOR MODAL */}
      {subModalOpen && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center p-4 z-50">
          <div className="bg-theme-panel border border-theme-border w-full max-w-md rounded-3xl p-6 shadow-2xl relative">
            <button 
              onClick={() => setSubModalOpen(false)}
              className="absolute right-4 top-4 text-slate-500 hover:text-theme-text-primary p-2 hover:bg-theme-border/20 rounded-xl"
            >
              <X size={20} />
            </button>
            <h3 className="text-xl font-bold mb-4">Registrar Nuevo Contratista</h3>
            
            <form onSubmit={handleCreateSubcontractor} className="space-y-4">
              <div>
                <label className="block text-xs font-bold uppercase text-theme-text-secondary mb-1">Razón Social / Nombre</label>
                <input
                  required
                  type="text"
                  placeholder="Ej: Pinturas Especializadas S.A."
                  value={newSubName}
                  onChange={(e) => setNewSubName(e.target.value)}
                  className="w-full bg-theme-bg border border-theme-border rounded-xl p-2.5 text-sm text-theme-text-primary focus:outline-none focus:border-emerald-500/50"
                />
              </div>

              <div>
                <label className="block text-xs font-bold uppercase text-theme-text-secondary mb-1">Especialidad</label>
                <input
                  required
                  type="text"
                  placeholder="Ej: Acabados, Plomería, Estructural"
                  value={newSubSpecialty}
                  onChange={(e) => setNewSubSpecialty(e.target.value)}
                  className="w-full bg-theme-bg border border-theme-border rounded-xl p-2.5 text-sm text-theme-text-primary focus:outline-none focus:border-emerald-500/50"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold uppercase text-theme-text-secondary mb-1">Supervisor</label>
                  <input
                    type="text"
                    placeholder="Nombre completo"
                    value={newSubSupervisor}
                    onChange={(e) => setNewSubSupervisor(e.target.value)}
                    className="w-full bg-theme-bg border border-theme-border rounded-xl p-2.5 text-sm text-theme-text-primary focus:outline-none focus:border-emerald-500/50"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase text-theme-text-secondary mb-1">Teléfono</label>
                  <input
                    type="text"
                    placeholder="Contacto"
                    value={newSubPhone}
                    onChange={(e) => setNewSubPhone(e.target.value)}
                    className="w-full bg-theme-bg border border-theme-border rounded-xl p-2.5 text-sm text-theme-text-primary focus:outline-none focus:border-emerald-500/50"
                  />
                </div>
              </div>

              <button
                type="submit"
                className="w-full bg-emerald-600 hover:bg-emerald-700 py-3 text-white rounded-xl font-bold shadow-md transition-colors"
              >
                Registrar Contratista
              </button>
            </form>
          </div>
        </div>
      )}

      {/* NEW WORKER MODAL */}
      {workerModalOpen && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center p-4 z-50">
          <div className="bg-theme-panel border border-theme-border w-full max-w-md rounded-3xl p-6 shadow-2xl relative">
            <button 
              onClick={() => setWorkerModalOpen(false)}
              className="absolute right-4 top-4 text-slate-500 hover:text-theme-text-primary p-2 hover:bg-theme-border/20 rounded-xl"
            >
              <X size={20} />
            </button>
            <h3 className="text-xl font-bold mb-4">Registrar Nuevo Trabajador</h3>
            
            <form onSubmit={handleCreateWorker} className="space-y-4">
              <div>
                <label className="block text-xs font-bold uppercase text-theme-text-secondary mb-1">Contratista</label>
                <select
                  required
                  value={newWorkerSubId}
                  onChange={(e) => setNewWorkerSubId(e.target.value)}
                  className="w-full bg-theme-bg border border-theme-border rounded-xl p-2.5 text-sm text-theme-text-primary focus:outline-none focus:border-emerald-500/50"
                >
                  <option value="">Selecciona contratista</option>
                  {subcontractors.map(sub => (
                    <option key={sub.id} value={sub.id}>{sub.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold uppercase text-theme-text-secondary mb-1">Nombre Completo</label>
                <input
                  required
                  type="text"
                  placeholder="Nombre del operario"
                  value={newWorkerName}
                  onChange={(e) => setNewWorkerName(e.target.value)}
                  className="w-full bg-theme-bg border border-theme-border rounded-xl p-2.5 text-sm text-theme-text-primary focus:outline-none focus:border-emerald-500/50"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold uppercase text-theme-text-secondary mb-1">Puesto / Rol</label>
                  <input
                    required
                    type="text"
                    placeholder="Ej: Fierrero, Albañil"
                    value={newWorkerRole}
                    onChange={(e) => setNewWorkerRole(e.target.value)}
                    className="w-full bg-theme-bg border border-theme-border rounded-xl p-2.5 text-sm text-theme-text-primary focus:outline-none focus:border-emerald-500/50"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase text-theme-text-secondary mb-1">NSS / ID EHS</label>
                  <input
                    type="text"
                    placeholder="Opcional"
                    value={newWorkerNss}
                    onChange={(e) => setNewWorkerNss(e.target.value)}
                    className="w-full bg-theme-bg border border-theme-border rounded-xl p-2.5 text-sm text-theme-text-primary focus:outline-none focus:border-emerald-500/50"
                  />
                </div>
              </div>

              <button
                type="submit"
                className="w-full bg-emerald-600 hover:bg-emerald-700 py-3 text-white rounded-xl font-bold shadow-md transition-colors"
              >
                Registrar Trabajador
              </button>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default AttendanceDashboard;
